<template>
  <div class="observation-form-container">
    <div class="form-header">
      <h2 class="form-title">Nouvelle observation</h2>
      <div class="header-actions">
        <button type="button" class="btn-example" @click="toggleExampleModal">
          <span class="icon">💡</span> Voir un exemple
        </button>
        <button class="btn-close" @click="$emit('close')" aria-label="Fermer">✕</button>
      </div>
    </div>

    <!-- MODE VOCAL -->
    <div v-if="!hideVocalMode" class="vocal-mode-container">
      <button
        type="button"
        class="btn-toggle-vocal"
        @click="showVocalMode = !showVocalMode"
        :class="{ active: showVocalMode }"
      >
        <span class="icon">🎤</span>
        <span>{{ showVocalMode ? 'Masquer' : 'Créer par' }} le mode vocal</span>
        <span class="toggle-arrow">{{ showVocalMode ? '▼' : '▶' }}</span>
      </button>

      <transition name="slide-fade">
        <div v-if="showVocalMode" class="vocal-mode-panel">
          <div class="vocal-header">
            <h3 class="vocal-title">
              <span class="icon">🎙️</span>
              Enregistrement vocal de l'observation
            </h3>
            <p class="vocal-description">
              Racontez l'observation complète de manière naturelle. L'IA analysera votre description
              et pré-remplira automatiquement les champs du formulaire.
            </p>
          </div>

          <!-- Warning si Mistral non configuré -->
          <div v-if="!mistralConfigured" class="warning-box">
            <span class="warning-icon">⚠️</span>
            <div class="warning-content">
              <strong>API Mistral non configurée</strong>
              <p>L'analyse IA automatique n'est pas disponible. Vous pouvez toujours enregistrer, mais devrez remplir le formulaire manuellement.</p>
            </div>
          </div>

          <!-- Recording Controls -->
          <div class="recording-controls">
            <button
              type="button"
              class="btn-record"
              :class="{ recording: isVocalRecording, disabled: isAnalyzing }"
              @click="toggleVocalRecording"
              :disabled="isAnalyzing"
            >
              <span class="record-icon" :class="{ pulse: isVocalRecording }">
                {{ isVocalRecording ? '⏸️' : '🎤' }}
              </span>
              <span class="record-text">
                {{ isVocalRecording ? 'Arrêter l\'enregistrement' : 'Commencer l\'enregistrement' }}
              </span>
            </button>

            <button
              v-if="vocalTranscript"
              type="button"
              class="btn-clear"
              @click="clearVocalTranscript"
              :disabled="isAnalyzing"
            >
              <span class="icon">🗑️</span>
              Effacer
            </button>
          </div>

          <!-- Recording Status -->
          <div v-if="isVocalRecording" class="recording-status">
            <span class="status-dot"></span>
            <span class="status-text">Enregistrement en cours... Parlez maintenant</span>
          </div>

          <!-- Transcript Display -->
          <div v-if="vocalTranscript" class="transcript-container">
            <div class="transcript-header">
              <label for="vocal-transcript-edit" class="transcript-label">
                <span class="icon">📝</span>
                Transcription (modifiable)
              </label>
              <span class="transcript-length">{{ vocalTranscript.length }} caractères</span>
            </div>
            <textarea
              id="vocal-transcript-edit"
              v-model="vocalTranscript"
              class="transcript-box editable"
              rows="8"
              placeholder="Votre transcription apparaîtra ici. Vous pouvez la modifier avant l'analyse..."
              :disabled="isAnalyzing"
            ></textarea>

            <!-- Analyze Button -->
            <button
              type="button"
              class="btn-analyze"
              @click="analyzeVocalTranscript"
              :disabled="isAnalyzing || !mistralConfigured"
              :class="{ analyzing: isAnalyzing }"
            >
              <span v-if="!isAnalyzing" class="icon">🤖</span>
              <span v-else class="spinner"></span>
              <span>{{ isAnalyzing ? 'Analyse en cours...' : 'Analyser avec l\'IA' }}</span>
            </button>
          </div>

          <!-- Helper Text -->
          <div class="helper-tips">
            <p class="tip-title"><strong>💡 Conseils pour un meilleur résultat :</strong></p>
            <ul class="tips-list">
              <li>Mentionnez la date, l'heure et le lieu de l'observation</li>
              <li>Décrivez l'état du jeune avant, pendant et après la situation</li>
              <li>Indiquez les personnes présentes et votre rôle d'observateur</li>
              <li>Détaillez les compétences manifestées et le contexte</li>
              <li>Parlez clairement et naturellement</li>
            </ul>
          </div>
        </div>
      </transition>
    </div>

    <!-- Bannière de confirmation du remplissage vocal -->
    <transition name="slide-down">
      <div v-if="showVocalFillBanner" class="vocal-fill-banner">
        <div class="banner-icon">✅</div>
        <div class="banner-content">
          <h4 class="banner-title">Formulaire pré-rempli avec succès !</h4>
          <p class="banner-text">
            Les champs ont été complétés automatiquement à partir de votre enregistrement vocal.
            <strong>Vérifiez et modifiez les informations si nécessaire</strong> avant de soumettre.
          </p>
        </div>
        <button type="button" class="banner-close" @click="showVocalFillBanner = false" aria-label="Fermer">
          ✕
        </button>
      </div>
    </transition>

    <form @submit.prevent.stop="handleSubmit" class="observation-form">
      <!-- 1. INFORMATIONS GÉNÉRALES -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">📅</span>
          Informations générales
        </h3>

        <div class="form-group">
          <fieldset>
            <legend class="form-label required">
              Source de l'observation
              <span class="info-tooltip tooltip-left" data-tooltip="Indiquez si vous avez observé la situation vous-même ou si elle vous a été rapportée par quelqu'un d'autre.">ℹ️</span>
            </legend>
            <div class="radio-group">
              <label class="radio-label">
                <input
                  id="obs-source-direct"
                  name="obs-source"
                  v-model="formData.observationSource"
                  type="radio"
                  value="direct"
                  required
                />
                <span class="radio-text">Je suis l'observateur (Observation directe)</span>
              </label>
              <label class="radio-label">
                <input
                  id="obs-source-indirect"
                  name="obs-source"
                  v-model="formData.observationSource"
                  type="radio"
                  value="indirect"
                  required
                />
                <span class="radio-text">Rapporté par un tiers (Observation indirecte)</span>
              </label>
            </div>
          </fieldset>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="obs-date" class="form-label required">
              Date de l'observation
              <span class="info-tooltip tooltip-left" data-tooltip="La date permet de suivre la chronologie et l'évolution des compétences dans le temps.">ℹ️</span>
            </label>
            <input
              id="obs-date"
              name="obs-date"
              v-model="formData.date"
              type="date"
              class="form-input"
              required
              :max="today"
            />
          </div>

          <div class="form-group">
            <label for="obs-start-time" class="form-label">
              Heure de début
              <span class="info-tooltip" data-tooltip="Le moment de la journée (matin/soir) influence la fatigue et la disponibilité du jeune.">ℹ️</span>
            </label>
            <input
              id="obs-start-time"
              name="obs-start-time"
              v-model="formData.startTime"
              type="time"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="obs-end-time" class="form-label">
              Heure de fin
              <span class="info-tooltip tooltip-right" data-tooltip="La durée de l'activité est un indicateur important de l'endurance et de la concentration.">ℹ️</span>
            </label>
            <input
              id="obs-end-time"
              name="obs-end-time"
              v-model="formData.endTime"
              type="time"
              class="form-input"
            />
          </div>
        </div>

        <div class="duration-display" v-if="duration">
          <span class="duration-icon">⏱️</span>
          <span class="duration-text">Durée totale : {{ duration }}</span>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label for="obs-people-present" class="form-label">
              Autres personnes présentes
              <span class="info-tooltip tooltip-left" data-tooltip="Les interactions sociales peuvent faciliter ou perturber la mise en œuvre de la compétence.">ℹ️</span>
            </label>
            <div class="tag-input-container">
              <div class="tags-list">
                <span
                  v-for="(person, index) in formData.peoplePresent"
                  :key="index"
                  class="tag"
                >
                  {{ person }}
                  <button
                    type="button"
                    @click="removePerson(index)"
                    class="tag-remove"
                    aria-label="Retirer"
                  >
                    ×
                  </button>
                </span>
              </div>
              <input
                id="obs-people-present"
                name="obs-people-present"
                v-model="newPerson"
                type="text"
                class="form-input"
                placeholder="Ajouter une personne (Entrée pour ajouter)"
                @keydown.enter.prevent="addPerson"
              />
            </div>
          </div>
        </div>

        <div class="form-group">
          <fieldset>
            <legend class="form-label required">
              Position de l'observateur
              <span class="info-tooltip tooltip-left" data-tooltip="Votre niveau d'implication (acteur ou spectateur) modifie la dynamique de la situation observée.">ℹ️</span>
            </legend>
            <div class="radio-group">
              <label class="radio-label">
                <input
                  id="obs-position-active"
                  name="obs-position"
                  v-model="formData.observerPosition"
                  type="radio"
                  value="active"
                  required
                />
                <span class="radio-text">Actif (participant à la situation)</span>
              </label>
              <label class="radio-label">
                <input
                  id="obs-position-passive"
                  name="obs-position"
                  v-model="formData.observerPosition"
                  type="radio"
                  value="passive"
                  required
                />
                <span class="radio-text">Passif (observateur extérieur)</span>
              </label>
            </div>
          </fieldset>
        </div>
      </section>

      <!-- 2. CONTEXTE ENVIRONNEMENTAL -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">🌍</span>
          Contexte environnemental
        </h3>

        <div class="form-row">
          <div class="form-group">
            <fieldset>
            <legend class="form-label required">
              Lieu
              <span class="info-tooltip tooltip-left" data-tooltip="L'environnement (intérieur/extérieur) influence les capacités sensorielles et motrices.">ℹ️</span>
            </legend>
            <div class="radio-group">
              <label class="radio-label">
                <input
                  id="obs-location-interior"
                  name="obs-location-type"
                  v-model="formData.location.type"
                  type="radio"
                  value="interior"
                  required
                />
                <span class="radio-text">Intérieur</span>
              </label>
              <label class="radio-label">
                <input
                  id="obs-location-exterior"
                  name="obs-location-type"
                  v-model="formData.location.type"
                  type="radio"
                  value="exterior"
                  required
                />
                <span class="radio-text">Extérieur</span>
              </label>
            </div>
          </fieldset>
          </div>

          <div class="form-group">
            <fieldset>
            <legend class="form-label required">
              Sphère
              <span class="info-tooltip tooltip-left" data-tooltip="La distinction sphère privée/publique permet d'évaluer l'adaptation aux codes sociaux.">ℹ️</span>
            </legend>
            <div class="radio-group">
              <label class="radio-label">
                <input
                  id="obs-sphere-private"
                  name="obs-sphere"
                  v-model="formData.environment.socialSphere"
                  type="radio"
                  value="privee"
                  required
                />
                <span class="radio-text">Privée (Maison, famille)</span>
              </label>
              <label class="radio-label">
                <input
                  id="obs-sphere-public"
                  name="obs-sphere"
                  v-model="formData.environment.socialSphere"
                  type="radio"
                  value="publique"
                  required
                />
                <span class="radio-text">Publique (École, parc, magasin)</span>
              </label>
            </div>
          </fieldset>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label for="obs-location-desc" class="form-label required">
              Description précise du lieu
              <span class="info-tooltip tooltip-left" data-tooltip="Le contexte spatial (ex: classe bruyante vs parc calme) aide à comprendre les déclencheurs ou facilitateurs.">ℹ️</span>
            </label>
            <input
              id="obs-location-desc"
              name="obs-location-desc"
              v-model="formData.location.description"
              type="text"
              class="form-input"
              placeholder="Ex: Salle de classe, parc municipal, cuisine..."
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="obs-noise-level" class="form-label required">
              Niveau sonore
              <span class="info-tooltip tooltip-left" data-tooltip="Le bruit peut être une source majeure de distraction ou de surcharge sensorielle.">ℹ️</span>
            </label>
            <select id="obs-noise-level" name="obs-noise-level" v-model="formData.environment.noiseLevel" class="form-select" required>
              <option value="">Sélectionner...</option>
              <option value="calme">Calme</option>
              <option value="modere">Modéré</option>
              <option value="bruyant">Bruyant</option>
            </select>
          </div>

          <div class="form-group">
            <label for="obs-lighting" class="form-label required">
              Luminosité
              <span class="info-tooltip" data-tooltip="L'éclairage (néons, soleil direct) peut impacter la fatigue visuelle ou l'attention.">ℹ️</span>
            </label>
            <select id="obs-lighting" name="obs-lighting" v-model="formData.environment.lighting" class="form-select" required>
              <option value="">Sélectionner...</option>
              <option value="tamisee">Tamisée</option>
              <option value="normale">Normale</option>
              <option value="forte">Forte</option>
            </select>
          </div>

          <div class="form-group">
            <label for="obs-people-count" class="form-label required">
              Densité sociale / Foule
              <span class="info-tooltip tooltip-right" data-tooltip="La sensation de foule ou de densité est plus pertinente que le nombre exact pour évaluer le stress social.">ℹ️</span>
            </label>
            <select id="obs-people-count" name="obs-people-count" v-model="formData.environment.peopleCount" class="form-select" required>
              <option value="">Sélectionner...</option>
              <option value="seul">Seul</option>
              <option value="petit">Petit groupe (calme)</option>
              <option value="dense">Groupe dense</option>
              <option value="foule">Foule (sensation de monde)</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="obs-other-sensory" class="form-label">
            Autres éléments sensoriels notables
            <span class="info-tooltip tooltip-left" data-tooltip="Odeurs, températures ou textures peuvent être des facteurs invisibles expliquant un comportement.">ℹ️</span>
          </label>
          <textarea
            id="obs-other-sensory"
            name="obs-other-sensory"
            v-model="formData.environment.otherSensory"
            class="form-textarea"
            rows="2"
            placeholder="Odeurs, température, textures..."
          ></textarea>
        </div>
      </section>

      <!-- 3. ÉTAT AVANT LA SITUATION -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">🔍</span>
          État avant la situation
        </h3>

        <div class="subsection">
          <h4 class="subsection-title">Le jeune</h4>

          <div class="form-row">
            <div class="form-group">
              <label for="obs-young-emotional" class="form-label required">
                État émotionnel
                <span class="info-tooltip tooltip-left" data-tooltip="L'humeur de départ conditionne la disponibilité et la réussite de l'activité.">ℹ️</span>
              </label>
              <select id="obs-young-emotional" name="obs-young-emotional" v-model="formData.youngBefore.emotionalState" class="form-select" required>
                <option value="">Sélectionner...</option>
                <option value="positif">Positif (Joyeux, Calme, Motivé...)</option>
                <option value="negatif">Négatif (Triste, Colérique, Anxieux...)</option>
                <option value="neutre">Neutre / Je ne sais pas</option>
              </select>
            </div>

            <div class="form-group">
              <label for="obs-young-behavior" class="form-label">
                Comportement général
                <span class="info-tooltip tooltip-right" data-tooltip="Observer l'agitation ou la passivité permet d'anticiper les réactions.">ℹ️</span>
              </label>
              <input
                id="obs-young-behavior"
                name="obs-young-behavior"
                v-model="formData.youngBefore.behavior"
                type="text"
                class="form-input"
                placeholder="Ex: Agité, posé, bavard..."
              />
            </div>
          </div>

          <div class="form-group">
            <fieldset>
              <legend class="form-label">
                Besoins physiologiques
                <span class="info-tooltip tooltip-left" data-tooltip="Cochez les besoins qui semblent satisfaits ou les actions réalisées récemment.">ℹ️</span>
              </legend>
              <div class="checkbox-group horizontal-wrap">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngBefore.physiologicalNeeds" value="a_mange">
                  <span class="checkbox-text">A mangé</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngBefore.physiologicalNeeds" value="a_dormi">
                  <span class="checkbox-text">A bien dormi</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngBefore.physiologicalNeeds" value="est_repose">
                  <span class="checkbox-text">Est reposé</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngBefore.physiologicalNeeds" value="a_ete_toilettes">
                  <span class="checkbox-text">A été aux toilettes</span>
                </label>
              </div>
            </fieldset>
          </div>

          <div class="form-group">
            <label for="obs-young-prep" class="form-label">
              Préparation à la situation
              <span class="info-tooltip tooltip-left" data-tooltip="La prévisibilité réduit l'anxiété. Savoir s'il a été préparé aide à évaluer son adaptabilité.">ℹ️</span>
            </label>
            <textarea
              id="obs-young-prep"
              name="obs-young-prep"
              v-model="formData.youngBefore.preparation"
              class="form-textarea"
              rows="2"
              placeholder="Lui a-t-on expliqué ce qui allait se passer ? Était-il préparé ?"
            ></textarea>
          </div>
        </div>

        <div class="subsection">
          <h4 class="subsection-title">L'observateur</h4>

          <div class="form-row">
            <div class="form-group">
              <fieldset>
                <legend class="form-label">
                  Votre état émotionnel
                  <span class="info-tooltip tooltip-left" data-tooltip="Votre propre état influence votre perception. Sélectionnez tout ce qui s'applique.">ℹ️</span>
                </legend>
                <div class="checkbox-group horizontal-wrap">
                  <label class="checkbox-label" v-for="emotion in observerEmotionsList" :key="emotion">
                    <input type="checkbox" v-model="formData.observerBefore.emotionalStates" :value="emotion">
                    <span class="checkbox-text">{{ emotion }}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            <div class="form-group">
              <label for="obs-observer-availability" class="form-label">
                Disponibilité pour le jeune
                <span class="info-tooltip tooltip-right" data-tooltip="Aviez-vous l'esprit et le temps nécessaire pour accompagner pleinement le jeune ?">ℹ️</span>
              </label>
              <select id="obs-observer-availability" name="obs-observer-availability" v-model="formData.observerBefore.availability" class="form-select">
                <option value="">Sélectionner...</option>
                <option value="totale">Totalement disponible</option>
                <option value="partielle">Partiellement disponible</option>
                <option value="limitee">Disponibilité limitée</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <!-- 4. DESCRIPTION FACTUELLE DE LA SITUATION -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">📝</span>
          Description factuelle de la situation
        </h3>

        <!-- Alert for sensitive data -->
        <div class="alert-warning" style="margin: 0 0 1.5rem 0;">
          <span class="alert-icon">⚠️</span>
          <p>
            <strong>Attention aux données sensibles :</strong> Ne pas inclure d'informations pouvant être interprétées
            comme dangereuses ou maltraitantes. Restez factuel et objectif.
          </p>
        </div>

        <div class="form-group">
          <div class="form-group-header">
            <label for="obs-factual-desc" class="form-label required">
              Les faits observés
              <span class="info-tooltip tooltip-left" data-tooltip="Décrire les actions sans jugement ni interprétation permet une analyse objective par toute l'équipe.">ℹ️</span>
            </label>
            <button
              type="button"
              class="btn-audio-standalone"
              @click="toggleAudioRecording"
              :class="{ recording: isRecording }"
            >
              <span class="audio-icon">🎙️</span>
              {{ isRecording ? 'Arrêter' : 'Note vocale' }}
            </button>
          </div>
          <textarea
            id="obs-factual-desc"
            name="obs-factual-desc"
            v-model="formData.factualDescription"
            class="form-textarea large"
            rows="8"
            required
            placeholder="Décrivez uniquement ce qui s'est passé, sans interprétation. Utilisez le passé. Soyez précis et objectif.&#10;&#10;Exemple : 'Le jeune est entré dans la pièce à 14h15. Il s'est dirigé vers le matériel sans assistance. Il a pris les outils et a commencé à travailler en silence pendant 10 minutes...'"
          ></textarea>
          <p class="char-count">{{ formData.factualDescription.length }} caractères</p>
        </div>
      </section>

      <!-- 5. INTERVENTIONS DE L'ACCOMPAGNANT -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">🤝</span>
          Interventions de l'accompagnant
        </h3>

        <div class="form-group">
          <fieldset><legend class="form-label">
            Type d'intervention
            <span class="info-tooltip tooltip-left" data-tooltip="Identifier le niveau d'aide fourni est essentiel pour évaluer le degré réel d'autonomie du jeune.">ℹ️</span>
          </legend>
          <div class="checkbox-group">
            <label class="checkbox-label">
              <input
                id="obs-intervention-none"
                name="obs-intervention-types"
                v-model="formData.interventions.types"
                type="checkbox"
                value="aucune"
              />
              <span class="checkbox-text">Aucune</span>
            </label>
            <label class="checkbox-label">
              <input
                id="obs-intervention-verbal"
                name="obs-intervention-types"
                v-model="formData.interventions.types"
                type="checkbox"
                value="guidage_verbal"
              />
              <span class="checkbox-text">Intervention verbale (Rappel, consigne...)</span>
            </label>
            <label class="checkbox-label">
              <input
                id="obs-intervention-physical"
                name="obs-intervention-types"
                v-model="formData.interventions.types"
                type="checkbox"
                value="guidage_physique"
              />
              <span class="checkbox-text">Intervention physique (Geste, tenue de main...)</span>
            </label>
            <label class="checkbox-label">
              <input
                id="obs-intervention-encouragement"
                name="obs-intervention-types"
                v-model="formData.interventions.types"
                type="checkbox"
                value="encouragement"
              />
              <span class="checkbox-text">Encouragement</span>
            </label>
          </div>
          </fieldset>
        </div>

        <div class="form-group">
          <label for="obs-intervention-desc" class="form-label">
            Description des interventions
            <span class="info-tooltip tooltip-right" data-tooltip="Préciser comment vous avez aidé (ex: 'j'ai tenu sa main', 'j'ai rappelé la consigne') permet d'ajuster l'accompagnement futur.">ℹ️</span>
          </label>
          <textarea
            id="obs-intervention-desc"
            name="obs-intervention-desc"
            v-model="formData.interventions.description"
            class="form-textarea"
            rows="4"
            placeholder="Ex: J'ai dû répéter la consigne en la simplifiant, puis j'ai guidé sa main pour initier le mouvement..."
          ></textarea>
        </div>
      </section>

      <!-- 6. ÉTAT APRÈS LA SITUATION -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">✨</span>
          État après la situation
        </h3>

        <div class="subsection">
          <h4 class="subsection-title">Le jeune</h4>

          <div class="form-row">
            <div class="form-group">
              <label for="obs-young-after-emotional" class="form-label required">
                État émotionnel
                <span class="info-tooltip tooltip-left" data-tooltip="Le ressenti final (fierté, frustration) détermine souvent la motivation pour recommencer l'activité.">ℹ️</span>
              </label>
              <select id="obs-young-after-emotional" name="obs-young-after-emotional" v-model="formData.youngAfter.emotionalState" class="form-select" required>
                <option value="">Sélectionner...</option>
                <option value="calme">Calme 😌</option>
                <option value="joyeux">Joyeux 😊</option>
                <option value="fier">Fier 😎</option>
                <option value="fatigue">Fatigué 😴</option>
                <option value="frustre">Frustré 😤</option>
                <option value="soulage">Soulagé 😅</option>
                <option value="unknown">Je ne sais pas ❓</option>
              </select>
            </div>

            <div class="form-group">
              <label for="obs-young-after-behavior" class="form-label">
                Comportement
                <span class="info-tooltip tooltip-right" data-tooltip="Notez les éléments clés pour votre feedback. Encouragez le jeune à verbaliser son ressenti pour favoriser l'intégration de la compétence.">ℹ️</span>
              </label>
              <input
                id="obs-young-after-behavior"
                name="obs-young-after-behavior"
                v-model="formData.youngAfter.behavior"
                type="text"
                class="form-input"
                placeholder="Ex: Détendu, satisfait, agité..."
              />
            </div>
          </div>

          <div class="form-group">
            <fieldset>
              <legend class="form-label">
                Signes de surcharge / Décompensation
                <span class="info-tooltip tooltip-left" data-tooltip="Ces signes sont cruciaux pour évaluer l'effort fourni par le jeune. Ils peuvent survenir immédiatement ou plus tard.">ℹ️</span>
              </legend>
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngAfter.decompensationSigns" value="flapping">
                  <span class="checkbox-text">Flapping / Stéréotypie</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngAfter.decompensationSigns" value="auto_mutilation">
                  <span class="checkbox-text">Auto-mutilation</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngAfter.decompensationSigns" value="repli">
                  <span class="checkbox-text">Repli sur soi</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngAfter.decompensationSigns" value="refuge">
                  <span class="checkbox-text">Besoin de refuge / Couverture lestée</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="formData.youngAfter.decompensationSigns" value="autre">
                  <span class="checkbox-text">Autre signe de décompensation</span>
                </label>
              </div>
            </fieldset>
          </div>
        </div>

        <div class="subsection">
          <h4 class="subsection-title">Actions de l'accompagnant</h4>

          <div class="form-group">
            <fieldset><legend class="sr-only">Actions de l'accompagnant</legend>
            <legend class="form-label">
              Type de feedback donné
              <span class="info-tooltip tooltip-left" data-tooltip="Vos réactions immédiates (félicitations, débriefing) servent de renforcement positif pour le jeune.">ℹ️</span>
            </legend>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input
                  id="obs-action-felicitations"
                  name="obs-accompanist-actions"
                  v-model="formData.accompanistActions"
                  type="checkbox"
                  value="felicitations"
                />
                <span class="checkbox-text">Félicitations</span>
              </label>
              <label class="checkbox-label">
                <input
                  id="obs-action-encouragements"
                  name="obs-accompanist-actions"
                  v-model="formData.accompanistActions"
                  type="checkbox"
                  value="encouragements"
                />
                <span class="checkbox-text">Encouragements</span>
              </label>
              <label class="checkbox-label">
                <input
                  id="obs-action-debriefing"
                  name="obs-accompanist-actions"
                  v-model="formData.accompanistActions"
                  type="checkbox"
                  value="debriefing"
                />
                <span class="checkbox-text">Débriefing</span>
              </label>
              <label class="checkbox-label">
                <input
                  id="obs-action-autre"
                  name="obs-accompanist-actions"
                  v-model="formData.accompanistActions"
                  type="checkbox"
                  value="autre"
                />
                <span class="checkbox-text">Autre</span>
              </label>
            </div>
            </fieldset>
          </div>
        </div>
      </section>

      <!-- 7. COMPÉTENCES OBSERVÉES -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">⭐</span>
          Compétences observées
        </h3>

        <div class="form-group">
          <fieldset>
            <legend class="form-label required">
              Sélection des compétences
              <span class="info-tooltip tooltip-left" data-tooltip="Cochez les compétences mobilisées et notez-les de 1 à 5 étoiles.">ℹ️</span>
            </legend>
            <div class="competences-list">
              <div v-for="(category, index) in competenceCategories" :key="index" class="competence-category">
                <h4 class="category-header">{{ category.category }}</h4>
                <div class="competence-items-grid">
                  <div
                    v-for="competence in category.items"
                    :key="competence.key"
                    class="competence-item"
                    :class="{ 'is-selected': formData.competences.includes(competence.key) }"
                  >
                    <label class="competence-label">
                      <input
                        type="checkbox"
                        v-model="formData.competences"
                        :value="competence.key"
                      />
                      <span class="competence-name">
                        {{ competence.name }}
                        <span class="info-tooltip" :data-tooltip="competence.description">ℹ️</span>
                      </span>
                    </label>
                    
                    <div v-if="formData.competences.includes(competence.key)" class="competence-rating" @click.stop>
                      <span class="rating-label">Évaluation :</span>
                      <div class="stars">
                        <span 
                          v-for="star in 5" 
                          :key="star" 
                          class="star" 
                          :class="{ filled: (formData.competenceRatings[competence.key] || 0) >= star }"
                          @click="setRating(competence.key, star)"
                        >★</span>
                      </div>
                      <span v-if="formData.competenceRatings[competence.key]" class="rating-value">
                        {{ formData.competenceRatings[competence.key] }}/5
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        <div class="form-group" v-if="formData.competences.length > 0">
          <label for="obs-competences-context" class="form-label required">
            Contexte de manifestation
            <span class="info-tooltip tooltip-left" data-tooltip="Comprendre ce qui a permis la réussite (ou causé l'échec) aide à reproduire les conditions favorables.">ℹ️</span>
          </label>
          <textarea
            id="obs-competences-context"
            name="obs-competences-context"
            v-model="formData.competencesContext"
            class="form-textarea"
            rows="5"
            required
            placeholder="Dans quelles conditions précises la ou les compétences se sont-elles exprimées ?&#10;Quels sont les facteurs facilitateurs identifiés ?"
          ></textarea>
        </div>
      </section>

      <!-- 8. NOTES COMPLÉMENTAIRES -->
      <section class="form-section">
        <h3 class="section-title">
          <span class="section-icon">📋</span>
          Notes complémentaires
        </h3>

        <div class="form-group">
          <div class="form-group-header">
            <label for="obs-additional-notes" class="form-label">
              Informations additionnelles
              <span class="info-tooltip tooltip-left" data-tooltip="Ajoutez ici tout détail supplémentaire pertinent pour la compréhension globale de la situation.">ℹ️</span>
            </label>
            <button
              type="button"
              class="btn-audio-standalone"
              @click="toggleAudioRecordingNotes"
              :class="{ recording: isRecordingNotes }"
            >
              <span class="audio-icon">🎙️</span>
              {{ isRecordingNotes ? 'Arrêter' : 'Note vocale' }}
            </button>
          </div>
          <textarea
            id="obs-additional-notes"
            name="obs-additional-notes"
            v-model="formData.additionalNotes"
            class="form-textarea"
            rows="4"
            placeholder="Toute autre information pertinente..."
          ></textarea>
        </div>
      </section>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="button" class="btn-secondary" @click="$emit('close')">
          Annuler
        </button>
        <button type="button" class="btn-draft" @click="saveDraft">
          💾 Enregistrer brouillon
        </button>
        <button type="submit" class="btn-primary" :disabled="!isFormValid">
          Enregistrer l'observation
        </button>
      </div>
    </form>
    <!-- Example Modal -->
    <div v-if="showExampleModal" class="modal-overlay" @click="toggleExampleModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Exemple complet : Atelier Cuisine</h2>
          <button class="modal-close" @click="toggleExampleModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="example-full">
            <div class="example-section">
              <h3>📅 Informations générales</h3>
              <div class="example-grid">
                <div class="example-item"><strong>Source :</strong> Observation directe</div>
                <div class="example-item"><strong>Horaire :</strong> 14:00 - 14:45 (45 min)</div>
                <div class="example-item"><strong>Observateur :</strong> Actif (Participant)</div>
                <div class="example-item"><strong>Présents :</strong> Éducateur, 2 autres jeunes</div>
              </div>
            </div>

            <div class="example-section">
              <h3>🌍 Contexte</h3>
              <div class="example-grid">
                <div class="example-item"><strong>Lieu :</strong> Intérieur (Cuisine pédagogique)</div>
                <div class="example-item"><strong>Sphère :</strong> Publique</div>
                <div class="example-item"><strong>Ambiance :</strong> Bruit modéré, Lumière normale</div>
                <div class="example-item"><strong>Densité :</strong> Petit groupe (calme)</div>
              </div>
            </div>

            <div class="example-section">
              <h3>🔍 État initial</h3>
              <p><strong>Jeune :</strong> Joyeux, motivé ("J'ai faim !"). A mangé, est reposé.</p>
              <p><strong>Observateur :</strong> Calme et totalement disponible.</p>
            </div>

            <div class="example-section highlight">
              <h3>📝 Description factuelle</h3>
              <p class="example-text">
                "Thomas a enfilé son tablier sans rappel. Il a sorti les ingrédients (farine, sucre, œufs) en vérifiant la liste sur le tableau. Il a pesé la farine (200g) avec précision sur la balance digitale. <br><br>
                Il a eu du mal à casser les œufs (des coquilles sont tombées dans le bol) et a commencé à taper du poing sur la table en disant "c'est nul". Après mon intervention, il a soufflé fort, a retiré les coquilles avec une petite cuillère et a repris le mélange. Il a enfourné le plat avec précaution."
              </p>
            </div>

            <div class="example-section">
              <h3>🤝 Interventions</h3>
              <p><strong>Type :</strong> Intervention verbale & Encouragement.</p>
              <p><strong>Détail :</strong> J'ai verbalisé : "Ce n'est pas grave pour les coquilles, on peut les enlever". Je lui ai montré comment faire.</p>
            </div>

            <div class="example-section">
              <h3>✨ État final</h3>
              <p><strong>Jeune :</strong> Fier (a pris une photo du gâteau), Calme. Pas de signes de décompensation.</p>
              <p><strong>Action :</strong> Félicitations devant le groupe, Dégustation partagée.</p>
            </div>

            <div class="example-section">
              <h3>⭐ Compétences validées</h3>
              <ul class="example-tags">
                <li>Autonomie (4/5)</li>
                <li>Gestion émotionnelle (3/5)</li>
                <li>Persévérance (5/5)</li>
              </ul>
              <p class="context-note"><strong>Contexte :</strong> Activité connue (recette habituelle) mais avec un nouvel ustensile (batteur).</p>
            </div>
          </div>
          
          <div class="modal-footer-actions">
             <button class="btn-use-example" @click="fillWithExampleData">
               🪄 Remplir le formulaire avec cet exemple
             </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Confirmation Modal for Example Fill -->
    <div v-if="showConfirmFillModal" class="modal-overlay" @click="showConfirmFillModal = false">
      <div class="modal-content confirm-modal" @click.stop>
        <div class="modal-header">
          <h2>Remplir avec l'exemple ?</h2>
          <button class="modal-close" @click="showConfirmFillModal = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="confirm-content">
            <div class="confirm-icon">⚠️</div>
            <p>Attention, cette action va <strong>remplacer toutes les données</strong> que vous avez saisies dans le formulaire actuel.</p>
            <p>Voulez-vous vraiment continuer ?</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showConfirmFillModal = false">Annuler</button>
          <button class="btn-primary btn-danger" @click="confirmFill">
            Oui, remplacer les données
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mistralParserService from '@/services/mistralParser.service';

export default {
  name: 'ObservationForm',
  props: {
    youngPersonId: {
      type: Number,
      required: true
    },
    youngPersonName: {
      type: String,
      required: true
    },
    preselectedCompetence: {
      type: String,
      default: null
    },
    startInVocalMode: {
      type: Boolean,
      default: false
    },
    hideVocalMode: {
      type: Boolean,
      default: false
    },
    existingObservation: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      today: new Date().toISOString().split('T')[0],
      newPerson: '',
      isRecording: false,
      isRecordingNotes: false,
      showExampleModal: false,
      showConfirmFillModal: false,
      recognition: null,
      recordingTarget: null,
      observerEmotionsList: ['Calme', 'Stressé', 'Fatigué', 'Inquiet', 'Confiant', 'Agacé', 'Joyeux'],
      // Mode vocal
      showVocalMode: false,
      isVocalRecording: false,
      vocalTranscript: '',
      isAnalyzing: false,
      vocalRecognition: null,
      mistralConfigured: false,
      showVocalFillBanner: false,
      formData: {
        // Section 1: Informations générales
        observationSource: 'direct',
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        peoplePresent: [],
        observerPosition: '',

        // Section 2: Contexte environnemental
        location: {
          type: '',
          description: ''
        },
        environment: {
          socialSphere: '',
          noiseLevel: '',
          lighting: '',
          peopleCount: '',
          otherSensory: ''
        },

        // Section 3: État avant - Le jeune
        youngBefore: {
          emotionalState: '',
          behavior: '',
          physiologicalNeeds: [],
          preparation: ''
        },

        // Section 3: État avant - L'observateur
        observerBefore: {
          emotionalStates: [],
          availability: ''
        },

        // Section 4: Description factuelle
        factualDescription: '',

        // Section 5: Interventions
        interventions: {
          types: [],
          description: ''
        },

        // Section 6: État après
        youngAfter: {
          emotionalState: '',
          behavior: '',
          decompensationSigns: []
        },
        accompanistActions: [],

        // Section 7: Compétences
        competences: [],
        competenceRatings: {},
        competencesContext: '',

        // Section 8: Notes complémentaires
        additionalNotes: ''
      },
      competenceCategories: [
        {
          category: "1. Communication & Coopération",
          items: [
            { key: 'com_claire', name: 'Communication claire et précise', description: 'Capacité à transmettre des informations de manière exacte et structurée.' },
            { key: 'ecoute', name: 'Écoute et compréhension', description: 'Capacité à recevoir des messages, à comprendre les consignes et à demander des clarifications.' },
            { key: 'collaboration', name: 'Collaboration en équipe', description: 'Capacité à travailler avec les autres en respectant les règles communes.' },
            { key: 'gestion_interactions', name: 'Gestion des interactions', description: 'Capacité à échanger avec ses collègues et sa hiérarchie de façon appropriée.' }
          ]
        },
        {
          category: "2. Organisation & Rigueur",
          items: [
            { key: 'planification', name: 'Planification et anticipation', description: 'Capacité à organiser son travail en tenant compte des délais et des priorités.' },
            { key: 'rigueur', name: 'Rigueur et précision', description: 'Capacité à réaliser des tâches en suivant exactement les procédures sans erreurs.' },
            { key: 'execution', name: 'Exécution et concentration', description: 'Capacité à se concentrer sur les tâches et à les mener à bien avec soin.' },
            { key: 'verification', name: 'Vérification et contrôle', description: 'Capacité à relire, vérifier et s\'assurer de l\'exactitude de son travail.' }
          ]
        },
        {
          category: "3. Autonomie & Initiative",
          items: [
            { key: 'autonomie', name: 'Autonomie dans le travail', description: 'Capacité à réaliser ses tâches de manière indépendante.' },
            { key: 'demande_aide', name: 'Demande d\'aide appropriée', description: 'Capacité à identifier quand on a besoin d\'assistance et à la demander.' },
            { key: 'initiative', name: 'Prise d\'initiative', description: 'Capacité à proposer des idées ou des solutions pour résoudre des problèmes.' },
            { key: 'decision', name: 'Prise de décision', description: 'Capacité à faire des choix appropriés dans le cadre de son activité.' }
          ]
        },
        {
          category: "4. Adaptation & Persévérance",
          items: [
            { key: 'gestion_changement', name: 'Gestion du changement', description: 'Capacité à s\'adapter à des modifications d\'organisation ou d\'environnement.' },
            { key: 'flexibilite', name: 'Flexibilité situationnelle', description: 'Capacité à ajuster sa manière de travailler en fonction des situations.' },
            { key: 'perseverance', name: 'Persévérance et engagement', description: 'Capacité à maintenir son effort jusqu\'à l\'achèvement complet d\'une tâche.' },
            { key: 'motivation', name: 'Motivation et implication', description: 'Capacité à s\'investir dans son travail et à démontrer de l\'intérêt.' }
          ]
        },
        {
          category: "5. Gestion de soi",
          items: [
            { key: 'gestion_stress', name: 'Gestion du stress', description: 'Capacité à utiliser des stratégies pour maintenir son efficacité sous pression.' },
            { key: 'gestion_emotionnelle', name: 'Gestion émotionnelle', description: 'Capacité à gérer ses émotions dans le cadre professionnel.' },
            { key: 'prise_recul', name: 'Prise de recul', description: 'Capacité à analyser objectivement les situations avant de réagir.' },
            { key: 'reactivite', name: 'Réactivité maîtrisée', description: 'Capacité à répondre aux urgences en priorisant les actions.' }
          ]
        },
        {
          category: "6. Apprentissage & Curiosité",
          items: [
            { key: 'curiosite', name: 'Curiosité professionnelle', description: 'Capacité à chercher à comprendre et à s\'informer.' },
            { key: 'apprentissage', name: 'Apprentissage continu', description: 'Capacité à acquérir de nouvelles connaissances nécessaires à son poste.' },
            { key: 'ouverture', name: 'Ouverture à la nouveauté', description: 'Capacité à accueillir de nouvelles méthodes ou outils.' }
          ]
        }
      ]
    };
  },
  computed: {
    duration() {
      if (!this.formData.startTime || !this.formData.endTime) return null;

      const start = new Date(`2000-01-01T${this.formData.startTime}`);
      const end = new Date(`2000-01-01T${this.formData.endTime}`);

      if (end <= start) return null;

      const diffMs = end - start;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}min`;
      }
      return `${minutes} minutes`;
    },
    isFormValid() {
      return (
        this.formData.observationSource &&
        this.formData.date &&
        this.formData.observerPosition &&
        this.formData.location.type &&
        this.formData.location.description &&
        this.formData.environment.socialSphere &&
        this.formData.environment.noiseLevel &&
        this.formData.environment.lighting &&
        this.formData.environment.peopleCount &&
        this.formData.youngBefore.emotionalState &&
        this.formData.youngAfter.emotionalState &&
        this.formData.factualDescription.trim() &&
        this.formData.competences.length > 0 &&
        this.formData.competencesContext.trim()
      );
    }
  },
  mounted() {
    // Clear old drafts due to structure change
    // In a real app, use versioning. Here we just clear to avoid bugs with new competences list.
    const draftKey = `observation_draft_${this.youngPersonId}`;
    localStorage.removeItem(draftKey);

    // Si on édite une observation existante, pré-remplir le formulaire
    if (this.existingObservation) {
      this.populateFormWithExistingData(this.existingObservation);
    }

    // Si une compétence est présélectionnée, l'ajouter
    if (this.preselectedCompetence) {
      this.formData.competences.push(this.preselectedCompetence);
    }

    // Vérifier si Mistral est configuré
    this.mistralConfigured = mistralParserService.isConfigured();

    // Initialiser la reconnaissance vocale pour le mode vocal
    this.initVocalRecognition();

    // Si startInVocalMode est true, ouvrir le mode vocal automatiquement
    if (this.startInVocalMode) {
      this.showVocalMode = true;
    }
  },
  methods: {
    setRating(competenceKey, rating) {
      // This ensures reactivity for new properties in nested objects
      this.formData.competenceRatings[competenceKey] = rating;
    },
    populateFormWithExistingData(observation) {
      // Pré-remplir le formulaire avec les données de l'observation existante
      this.formData = {
        observationSource: observation.observationSource || 'direct',
        date: observation.date || new Date().toISOString().split('T')[0],
        startTime: observation.startTime || '',
        endTime: observation.endTime || '',
        peoplePresent: observation.peoplePresent || [],
        observerPosition: observation.observerPosition || '',
        location: {
          type: observation.location?.type || '',
          description: observation.location?.description || ''
        },
        environment: {
          socialSphere: observation.environment?.socialSphere || '',
          noiseLevel: observation.environment?.noiseLevel || '',
          lighting: observation.environment?.lighting || '',
          peopleCount: observation.environment?.peopleCount || '',
          otherSensory: observation.environment?.otherSensory || ''
        },
        youngBefore: {
          emotionalState: observation.youngBefore?.emotionalState || '',
          behavior: observation.youngBefore?.behavior || '',
          physiologicalNeeds: observation.youngBefore?.physiologicalNeeds || [],
          preparation: observation.youngBefore?.preparation || ''
        },
        observerBefore: {
          emotionalStates: observation.observerBefore?.emotionalStates || [],
          availability: observation.observerBefore?.availability || ''
        },
        factualDescription: observation.factualDescription || '',
        interventions: {
          types: observation.interventions?.types || [],
          description: observation.interventions?.description || ''
        },
        youngAfter: {
          emotionalState: observation.youngAfter?.emotionalState || '',
          behavior: observation.youngAfter?.behavior || '',
          decompensationSigns: observation.youngAfter?.decompensationSigns || []
        },
        accompanistActions: observation.accompanistActions || [],
        competences: observation.competences || [],
        competenceRatings: observation.competenceRatings || {},
        competencesContext: observation.competencesContext || '',
        additionalNotes: observation.additionalNotes || '',
        youngPersonId: this.youngPersonId
      };
    },
    toggleExampleModal() {
      this.showExampleModal = !this.showExampleModal;
    },
    fillWithExampleData() {
      // Open confirmation modal instead of native confirm
      this.showConfirmFillModal = true;
    },
    confirmFill() {
      this.formData = {
        observationSource: 'direct',
        date: new Date().toISOString().split('T')[0],
        startTime: '14:00',
        endTime: '14:45',
        peoplePresent: ['Éducateur', '2 autres jeunes'],
        observerPosition: 'active',
        location: {
          type: 'interior',
          description: 'Cuisine pédagogique'
        },
        environment: {
          socialSphere: 'publique',
          noiseLevel: 'modere',
          lighting: 'normale',
          peopleCount: 'petit',
          otherSensory: 'Odeur de cuisson, chaleur du four'
        },
        youngBefore: {
          emotionalState: 'positif',
          behavior: 'Motivé ("J\'ai faim !")',
          physiologicalNeeds: ['a_mange', 'est_repose'],
          preparation: 'A lu la recette la veille'
        },
        observerBefore: {
          emotionalStates: ['Calme', 'Confiant'],
          availability: 'totale'
        },
        factualDescription: "Thomas a enfilé son tablier sans rappel. Il a sorti les ingrédients (farine, sucre, œufs) en vérifiant la liste sur le tableau. Il a pesé la farine (200g) avec précision sur la balance digitale.\n\nIl a eu du mal à casser les œufs (des coquilles sont tombées dans le bol) et a commencé à taper du poing sur la table en disant \"c'est nul\". Après mon intervention, il a soufflé fort, a retiré les coquilles avec une petite cuillère et a repris le mélange. Il a enfourné le plat avec précaution.",
        interventions: {
          types: ['guidage_verbal', 'encouragement'],
          description: "Ex: J'ai dû répéter la consigne en la simplifiant, puis j'ai guidé sa main pour initier le mouvement..."
        },
        youngAfter: {
          emotionalState: 'fier',
          behavior: 'A pris une photo du gâteau',
          decompensationSigns: []
        },
        accompanistActions: ['felicitations', 'autre'],
        competences: ['autonomie', 'gestion_emotionnelle', 'perseverance'],
        competenceRatings: {
          'autonomie': 4,
          'gestion_emotionnelle': 3,
          'perseverance': 5
        },
        competencesContext: 'Activité connue (recette habituelle) mais avec un nouvel ustensile (batteur).',
        additionalNotes: ''
      };
      
      this.showConfirmFillModal = false;
      this.showExampleModal = false; // Close the example modal too
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    addPerson() {
      const person = this.newPerson.trim();
      if (person && !this.formData.peoplePresent.includes(person)) {
        this.formData.peoplePresent.push(person);
        this.newPerson = '';
      }
    },
    removePerson(index) {
      this.formData.peoplePresent.splice(index, 1);
    },
    toggleAudioRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording('factualDescription');
      }
    },
    toggleAudioRecordingNotes() {
      if (this.isRecordingNotes) {
        this.stopRecording();
      } else {
        this.startRecording('additionalNotes');
      }
    },
    startRecording(target) {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        alert("Votre navigateur ne supporte pas la reconnaissance vocale.");
        return;
      }

      // Stop any ongoing recording
      this.stopRecording();

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.lang = 'fr-FR';
      this.recognition.continuous = true;
      this.recognition.interimResults = true;

      this.recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            const transcript = event.results[i][0].transcript.trim();
            if (transcript) {
                const field = this.recordingTarget;
                const currentText = this.formData[field] || '';
                const prefix = (currentText.length > 0 && !/\s$/.test(currentText)) ? ' ' : '';
                this.formData[field] = currentText + prefix + transcript;
            }
          }
        }
      };

      this.recognition.onerror = (event) => {
        console.error('Erreur reconnaissance vocale:', event.error);
        if (event.error === 'not-allowed') {
            alert("L'accès au microphone a été refusé. Veuillez l'autoriser dans les paramètres de votre navigateur.");
        }
        this.stopRecording();
      };
      
      this.recognition.onend = () => {
          if (this.isRecording || this.isRecordingNotes) {
             this.isRecording = false;
             this.isRecordingNotes = false;
          }
      };

      this.recordingTarget = target;
      if (target === 'factualDescription') this.isRecording = true;
      if (target === 'additionalNotes') this.isRecordingNotes = true;

      try {
        this.recognition.start();
      } catch (e) {
        console.error("Erreur démarrage:", e);
        this.stopRecording();
      }
    },
    stopRecording() {
      if (this.recognition) {
        this.recognition.stop();
        this.recognition = null;
      }
      this.isRecording = false;
      this.isRecordingNotes = false;
      this.recordingTarget = null;
    },
    saveDraft() {
      // Sauvegarder en local storage
      const draftKey = `observation_draft_${this.youngPersonId}`;
      localStorage.setItem(draftKey, JSON.stringify(this.formData));

      // Notification de succès
      alert('Brouillon enregistré avec succès !');
    },
    loadDraft() {
      const draftKey = `observation_draft_${this.youngPersonId}`;
      const savedDraft = localStorage.getItem(draftKey);

      if (savedDraft) {
        const shouldLoad = confirm('Un brouillon a été trouvé. Voulez-vous le charger ?');
        if (shouldLoad) {
          this.formData = JSON.parse(savedDraft);
        }
      }
    },
    clearDraft() {
      const draftKey = `observation_draft_${this.youngPersonId}`;
      localStorage.removeItem(draftKey);
    },
    handleSubmit() {
      if (!this.isFormValid) {
        alert('Veuillez remplir tous les champs obligatoires.');
        return;
      }

      // Préparer les données pour l'envoi
      const observationData = {
        youngPersonId: this.youngPersonId,
        ...this.formData,
        duration: this.duration,
        timestamp: new Date().toISOString()
      };

      // Émettre l'événement avec les données
      this.$emit('save', observationData);

      // Supprimer le brouillon après soumission réussie
      this.clearDraft();
    },

    // Méthodes pour le mode vocal
    initVocalRecognition() {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.warn('Speech recognition not supported');
        return;
      }

      this.vocalRecognition = new SpeechRecognition();
      this.vocalRecognition.lang = 'fr-FR';
      this.vocalRecognition.continuous = true;
      this.vocalRecognition.interimResults = true;

      this.vocalRecognition.onresult = (event) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }

        if (finalTranscript) {
          this.vocalTranscript = (this.vocalTranscript + ' ' + finalTranscript).trim();
        }
      };

      this.vocalRecognition.onerror = (event) => {
        console.error('Vocal recognition error:', event.error);
        this.isVocalRecording = false;
        if (event.error === 'no-speech') {
          alert('Aucune parole détectée. Veuillez réessayer.');
        } else if (event.error === 'not-allowed') {
          alert('Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur.');
        }
      };

      this.vocalRecognition.onend = () => {
        this.isVocalRecording = false;
      };
    },

    toggleVocalRecording() {
      if (!this.vocalRecognition) {
        alert('La reconnaissance vocale n\'est pas disponible sur votre navigateur.');
        return;
      }

      if (this.isVocalRecording) {
        this.vocalRecognition.stop();
        this.isVocalRecording = false;
      } else {
        this.vocalRecognition.start();
        this.isVocalRecording = true;
      }
    },

    clearVocalTranscript() {
      this.vocalTranscript = '';
    },

    async analyzeVocalTranscript() {
      if (!this.vocalTranscript.trim()) {
        alert('Veuillez d\'abord enregistrer votre observation.');
        return;
      }

      this.isAnalyzing = true;

      try {
        // Appeler le service Mistral pour analyser le texte
        const parsedData = await mistralParserService.parseCompleteObservation(
          this.vocalTranscript,
          this.competenceCategories
        );

        // Pré-remplir le formulaire avec les données analysées
        this.prefillFormWithParsedData(parsedData);

        // Fermer la section vocale immédiatement pour afficher le formulaire
        this.showVocalMode = false;

        // Afficher la bannière de succès
        this.showVocalFillBanner = true;

        // Attendre un peu pour que la transition se termine
        await new Promise(resolve => setTimeout(resolve, 400));

        // Scroller vers le début du formulaire
        this.$nextTick(() => {
          const formElement = document.querySelector('.observation-form');
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });

        // Masquer la bannière après 8 secondes
        setTimeout(() => {
          this.showVocalFillBanner = false;
        }, 8000);

      } catch (error) {
        console.error('Error analyzing vocal transcript:', error);
        alert('❌ Erreur lors de l\'analyse de votre observation.\n\nVous pouvez remplir le formulaire manuellement.');
      } finally {
        this.isAnalyzing = false;
      }
    },

    prefillFormWithParsedData(parsedData) {
      // Pré-remplir tous les champs du formulaire avec les données parsées
      this.formData.observationSource = parsedData.observationSource || this.formData.observationSource;
      this.formData.startTime = parsedData.startTime || this.formData.startTime;
      this.formData.endTime = parsedData.endTime || this.formData.endTime;
      this.formData.peoplePresent = parsedData.peoplePresent.length > 0 ? parsedData.peoplePresent : this.formData.peoplePresent;
      this.formData.observerPosition = parsedData.observerPosition || this.formData.observerPosition;

      // Location
      if (parsedData.location.type) {
        this.formData.location.type = parsedData.location.type;
      }
      if (parsedData.location.description) {
        this.formData.location.description = parsedData.location.description;
      }

      // Environment
      if (parsedData.environment.socialSphere) {
        this.formData.environment.socialSphere = parsedData.environment.socialSphere;
      }
      if (parsedData.environment.noiseLevel) {
        this.formData.environment.noiseLevel = parsedData.environment.noiseLevel;
      }
      if (parsedData.environment.lighting) {
        this.formData.environment.lighting = parsedData.environment.lighting;
      }
      if (parsedData.environment.peopleCount) {
        this.formData.environment.peopleCount = parsedData.environment.peopleCount;
      }
      if (parsedData.environment.otherSensory) {
        this.formData.environment.otherSensory = parsedData.environment.otherSensory;
      }

      // Young Before
      if (parsedData.youngBefore.emotionalState) {
        this.formData.youngBefore.emotionalState = parsedData.youngBefore.emotionalState;
      }
      if (parsedData.youngBefore.behavior) {
        this.formData.youngBefore.behavior = parsedData.youngBefore.behavior;
      }
      if (parsedData.youngBefore.physiologicalNeeds.length > 0) {
        this.formData.youngBefore.physiologicalNeeds = parsedData.youngBefore.physiologicalNeeds;
      }
      if (parsedData.youngBefore.preparation) {
        this.formData.youngBefore.preparation = parsedData.youngBefore.preparation;
      }

      // Observer Before
      if (parsedData.observerBefore.emotionalStates.length > 0) {
        this.formData.observerBefore.emotionalStates = parsedData.observerBefore.emotionalStates;
      }
      if (parsedData.observerBefore.availability) {
        this.formData.observerBefore.availability = parsedData.observerBefore.availability;
      }

      // Factual Description
      if (parsedData.factualDescription) {
        this.formData.factualDescription = parsedData.factualDescription;
      }

      // Interventions
      if (parsedData.interventions.types.length > 0) {
        this.formData.interventions.types = parsedData.interventions.types;
      }
      if (parsedData.interventions.description) {
        this.formData.interventions.description = parsedData.interventions.description;
      }

      // Young After
      if (parsedData.youngAfter.emotionalState) {
        this.formData.youngAfter.emotionalState = parsedData.youngAfter.emotionalState;
      }
      if (parsedData.youngAfter.behavior) {
        this.formData.youngAfter.behavior = parsedData.youngAfter.behavior;
      }
      if (parsedData.youngAfter.decompensationSigns.length > 0) {
        this.formData.youngAfter.decompensationSigns = parsedData.youngAfter.decompensationSigns;
      }

      // Accompanist Actions
      if (parsedData.accompanistActions.length > 0) {
        this.formData.accompanistActions = parsedData.accompanistActions;
      }

      // Competences
      if (parsedData.competences.length > 0) {
        this.formData.competences = parsedData.competences;
      }
      if (parsedData.competencesContext) {
        this.formData.competencesContext = parsedData.competencesContext;
      }

      // Additional Notes
      if (parsedData.additionalNotes) {
        this.formData.additionalNotes = parsedData.additionalNotes;
      }
    }
  }
};
</script>

<style scoped>
.observation-form-container {
  background: linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%);
  border-top-left-radius: 5px;
  border-top-right-radius: 0px;
  max-width: 1200px;
  margin: 0 auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2.5rem;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-bottom: none;
  position: relative;
  overflow: hidden;
}

.form-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.form-title {
  font-size: 1.875rem;
  font-weight: 800;
  color: white;
  margin: 0;
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.alert-warning {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border: 2px solid #F59E0B;
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  margin: 2rem 2.5rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
  animation: fadeIn 0.4s ease-out 0.2s both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert-icon {
  font-size: 1.75rem;
  flex-shrink: 0;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.alert-warning p {
  margin: 0;
  color: #92400E;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.alert-warning strong {
  font-weight: 700;
}

/* ===== VOCAL MODE STYLES ===== */
.vocal-mode-container {
  margin: 1.5rem 2.5rem 0;
}

.btn-toggle-vocal {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #F97316 0%, #FB923C 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.2);
}

.btn-toggle-vocal:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(249, 115, 22, 0.3);
}

.btn-toggle-vocal .icon {
  font-size: 1.25rem;
}

.btn-toggle-vocal .toggle-arrow {
  margin-left: auto;
  font-size: 0.875rem;
  opacity: 0.8;
}

.btn-toggle-vocal.active {
  background: linear-gradient(135deg, #EA580C 0%, #F97316 100%);
}

/* Slide Fade Transition */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from, .slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.vocal-mode-panel {
  margin-top: 1rem;
  padding: 2rem;
  background: linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%);
  border: 2px solid #FDBA74;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(249, 115, 22, 0.1);
}

.vocal-header {
  margin-bottom: 1.5rem;
}

.vocal-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: #9A3412;
  margin: 0 0 0.75rem 0;
}

.vocal-title .icon {
  font-size: 1.5rem;
}

.vocal-description {
  color: #9A3412;
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0;
  opacity: 0.9;
}

.warning-box {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #FEF2F2;
  border: 2px solid #FCA5A5;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.warning-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  color: #991B1B;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.warning-content p {
  color: #991B1B;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
}

.recording-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.btn-record {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  background: linear-gradient(135deg, #F97316 0%, #FB923C 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.25);
}

.btn-record:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(249, 115, 22, 0.35);
}

.btn-record.recording {
  background: linear-gradient(135deg, #DC2626 0%, #EF4444 100%);
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 6px 24px rgba(220, 38, 38, 0.6);
  }
}

.btn-record:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.record-icon {
  font-size: 1.75rem;
  display: inline-block;
}

.record-icon.pulse {
  animation: pulse-icon 1.5s ease-in-out infinite;
}

@keyframes pulse-icon {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

.btn-clear {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  color: #6B7280;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear:hover:not(:disabled) {
  background: #F9FAFB;
  border-color: #D1D5DB;
  color: #374151;
}

.btn-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recording-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #FEE2E2;
  border: 2px solid #FCA5A5;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.status-dot {
  width: 12px;
  height: 12px;
  background: #DC2626;
  border-radius: 50%;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.status-text {
  color: #991B1B;
  font-weight: 600;
  font-size: 0.9375rem;
}

.transcript-container {
  margin-bottom: 1.5rem;
}

.transcript-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.transcript-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #9A3412;
}

.transcript-label .icon {
  font-size: 1.125rem;
}

.transcript-length {
  font-size: 0.875rem;
  color: #9A3412;
  opacity: 0.7;
}

.transcript-box {
  padding: 1.5rem;
  background: white;
  border: 2px solid #FED7AA;
  border-radius: 12px;
  min-height: 120px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: #374151;
  white-space: pre-wrap;
  word-wrap: break-word;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.transcript-box.editable {
  width: 100%;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.transcript-box.editable:focus {
  outline: none;
  border-color: #FB923C;
  box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1), inset 0 2px 4px rgba(0, 0, 0, 0.05);
}

.transcript-box.editable:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #F9FAFB;
}

.btn-analyze {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  background: linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
  margin-top: 1rem;
}

.btn-analyze:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.35);
}

.btn-analyze:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-analyze.analyzing {
  background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%);
}

.btn-analyze .icon {
  font-size: 1.5rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.helper-tips {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.5);
  border: 2px dashed #FDBA74;
  border-radius: 12px;
}

.tip-title {
  color: #9A3412;
  font-size: 0.9375rem;
  margin: 0 0 0.75rem 0;
}

.tips-list {
  margin: 0;
  padding-left: 1.5rem;
  color: #9A3412;
  font-size: 0.875rem;
  line-height: 1.8;
  opacity: 0.9;
}

.tips-list li {
  margin-bottom: 0.375rem;
}

.tips-list li:last-child {
  margin-bottom: 0;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .vocal-mode-container {
    margin: 1rem 1.25rem 0;
  }

  .vocal-mode-panel {
    padding: 1.5rem;
  }

  .recording-controls {
    flex-direction: column;
  }

  .btn-record, .btn-clear {
    width: 100%;
  }
}

/* ===== VOCAL FILL BANNER ===== */
.vocal-fill-banner {
  margin: 1.5rem 2.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
  border: 2px solid #10B981;
  border-radius: 16px;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  animation: slideInDown 0.4s ease-out;
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.banner-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.banner-content {
  flex: 1;
}

.banner-title {
  color: #065F46;
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.banner-text {
  color: #047857;
  font-size: 0.9375rem;
  margin: 0;
  line-height: 1.6;
}

.banner-text strong {
  color: #065F46;
}

.banner-close {
  background: none;
  border: none;
  color: #059669;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.banner-close:hover {
  background: rgba(16, 185, 129, 0.2);
  color: #065F46;
}

/* Slide Down Transition pour la bannière */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

@media (max-width: 768px) {
  .vocal-fill-banner {
    margin: 1rem 1.25rem;
    padding: 1.25rem;
    flex-direction: column;
    text-align: center;
  }

  .banner-icon {
    font-size: 2.5rem;
  }

  .banner-close {
    align-self: center;
  }
}

.observation-form {
  padding: 2.5rem;
}

.form-section {
  margin-bottom: 3rem;
  padding-bottom: 2.5rem;
  border-bottom: 2px solid #F3F4F6;
  position: relative;
  animation: fadeInSection 0.5s ease-out both;
  animation-delay: calc(var(--section-index, 0) * 0.1s);
}

@keyframes fadeInSection {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.form-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 1.375rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 1.75rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #FF9800 0%, #FF9800 50%, transparent 50%);
  border-image-slice: 1;
}

.section-icon {
  font-size: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
  transition: transform 0.3s ease;
}

.section-title:hover .section-icon {
  transform: scale(1.1) rotate(5deg);
}

.subsection {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%);
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  transition: all 0.3s ease;
}

.subsection:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.subsection-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subsection-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 2px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.form-label.required::after {
  content: ' *';
  color: #EF4444;
}

/* Fieldset and Legend styles for accessibility */
fieldset {
  border: none;
  padding: 0;
  margin: 0;
}

legend {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  padding: 0;
  margin-bottom: 0.5rem;
}

legend.required::after {
  content: ' *';
  color: #EF4444;
}

legend.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.form-input,
.form-select,
.form-textarea {
  padding: 0.875rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
}

.form-input:hover,
.form-select:hover,
.form-textarea:hover {
  border-color: #D1D5DB;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #FF9800;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1), 0 2px 8px rgba(255, 152, 0, 0.15);
  transform: translateY(-1px);
}

.form-textarea {
  resize: vertical;
  min-height: 90px;
  line-height: 1.6;
}

.form-textarea.large {
  min-height: 160px;
}

.duration-display {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #FFF5F0 0%, #FFEDD5 100%);
  border: 2px solid #FB923C;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.1);
  animation: pulseGlow 2s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.1);
  }
  50% {
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2);
  }
}

.duration-icon {
  font-size: 1.5rem;
  animation: spin 3s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.duration-text {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #B85C00;
}

.tag-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  min-height: 32px;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  color: #B85C00;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #FB923C;
  box-shadow: 0 2px 4px rgba(255, 152, 0, 0.1);
  transition: all 0.3s ease;
  animation: tagIn 0.3s ease-out;
}

@keyframes tagIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(255, 152, 0, 0.15);
}

.tag-remove {
  background: rgba(255, 255, 255, 0.5);
  border: none;
  color: #B85C00;
  font-size: 1.125rem;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.tag-remove:hover {
  background: rgba(255, 255, 255, 0.9);
  color: #7A3E00;
  transform: rotate(90deg);
}

.radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding-bottom: 1.5rem;
}

.radio-label,
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid #F3F4F6;
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.radio-label:hover,
.checkbox-label:hover {
  background: #F9FAFB;
  border-color: #E5E7EB;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transform: translateX(4px);
}

.radio-label:has(input:checked),
.checkbox-label:has(input:checked) {
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  border-color: #FF9800;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);
}

.radio-label input[type="radio"],
.checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #FF9800;
}

.radio-text,
.checkbox-text {
  font-size: 0.9375rem;
  color: #374151;
  font-weight: 500;
  flex: 1;
}

.form-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Remove default margin from label when in header */
.form-group-header .form-label {
  margin-bottom: 0;
}

.btn-audio-standalone {
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-audio-standalone:hover {
  background: #F3F4F6;
  border-color: #D1D5DB;
  color: #111827;
}

.btn-audio-standalone.recording {
  background: #FEE2E2;
  border-color: #FCA5A5;
  color: #B91C1C;
  animation: pulse-recording 1.5s ease-in-out infinite;
}

@keyframes pulse-recording {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
}

.audio-icon {
  font-size: 1rem;
}

.char-count {
  font-size: 0.8125rem;
  color: #9CA3AF;
  text-align: right;
  margin: 0.375rem 0 0 0;
  font-weight: 500;
}

.competences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.competence-checkbox {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.competence-checkbox::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.competence-checkbox:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.competence-checkbox:hover::before {
  transform: scaleY(1);
}

.competence-checkbox:has(input:checked) {
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  border-color: #FF9800;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2);
}

.competence-checkbox:has(input:checked)::before {
  transform: scaleY(1);
}

.competence-checkbox input[type="checkbox"]:checked + .competence-name {
  font-weight: 700;
  color: #FFA726;
}

.competence-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #FF9800;
}

.competence-name {
  font-size: 0.9375rem;
  color: #374151;
  font-weight: 500;
  flex: 1;
  transition: all 0.3s ease;
}

.competences-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.competence-category {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-header {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #E5E7EB;
}

.competence-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.competence-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.competence-item.is-selected {
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  border-color: #FF9800;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);
}

.competence-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  width: 100%;
}

.competence-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #FF9800;
}

.competence-rating {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 152, 0, 0.3);
  margin-top: 0.25rem;
  animation: slideDown 0.3s ease;
}

.rating-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #B85C00;
}

.stars {
  display: flex;
  gap: 0.25rem;
}

.star {
  font-size: 1.75rem;
  color: #D1D5DB;
  cursor: pointer;
  transition: all 0.2s ease;
  text-shadow: none;
}

/* Empty star inside selected container */
.competence-item.is-selected .star {
  color: rgba(0, 0, 0, 0.15); /* Light grey for empty */
}

/* Filled/Hover star inside selected container - Stronger specificity */
.competence-item.is-selected .star:hover,
.competence-item.is-selected .star.filled {
  color: #EA580C; /* Burnt Orange / Deep Gold for filled */
  transform: scale(1.2);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.rating-value {
  font-weight: 700;
  color: #B45309;
  font-size: 0.9375rem;
  margin-left: 0.5rem;
  min-width: 30px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 2.5rem 2.5rem 2rem;
  margin-top: 2rem;
  background: linear-gradient(to top, #F9FAFB 0%, transparent 100%);
  border-top: 2px solid #F3F4F6;
}

.btn-primary,
.btn-secondary,
.btn-draft {
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: none;
  letter-spacing: 0.3px;
}

.btn-secondary {
  background: linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%);
  color: #374151;
  border: 2px solid #D1D5DB;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-draft {
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  color: #92400E;
  border: 2px solid #F59E0B;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.btn-draft:hover {
  background: linear-gradient(135deg, #FDE68A 0%, #FCD34D 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.3);
}

.btn-primary {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.4);
  position: relative;
  overflow: hidden;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.btn-primary:hover::before {
  left: 100%;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.5);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-1px) scale(1);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .form-actions {
    padding: 2rem 2rem 1.5rem;
  }

  .competences-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 768px) {
  .observation-form-container {
    border-radius: 0;
    box-shadow: none;
  }

  .form-header {
    padding: 1.5rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .observation-form {
    padding: 1.5rem;
  }

  .alert-warning {
    margin: 1.5rem 1.5rem;
    padding: 1rem 1.25rem;
  }

  .form-section {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
  }

  .section-title {
    font-size: 1.125rem;
    gap: 0.75rem;
  }

  .section-icon {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
  }

  .subsection {
    padding: 1.25rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }

  .competences-grid {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }

  .form-actions {
    flex-direction: column-reverse;
    gap: 0.875rem;
    padding: 1.5rem;
  }

  .btn-primary,
  .btn-secondary,
  .btn-draft {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.5rem;
  }

  .btn-audio {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  .tags-list {
    gap: 0.5rem;
  }

  .tag {
    font-size: 0.8125rem;
    padding: 0.375rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .form-header {
    padding: 1.25rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .btn-close {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }

  .observation-form {
    padding: 1.25rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .section-icon {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }

  .form-input,
  .form-select,
  .form-textarea {
    font-size: 16px; /* Empêche le zoom sur iOS */
    padding: 0.75rem 0.875rem;
  }

  .duration-display {
    padding: 0.875rem 1rem;
  }

  .radio-label,
  .checkbox-label {
    padding: 0.75rem 0.875rem;
  }

  .competence-checkbox {
    padding: 0.875rem;
  }
}

/* Amélioration pour les écrans haute résolution */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .form-input,
  .form-select,
  .form-textarea {
    border-width: 1.5px;
  }

  .competence-checkbox,
  .radio-label,
  .checkbox-label {
    border-width: 1.5px;
  }
}

/* Améliorations pour le mode sombre (si nécessaire plus tard) */
@media (prefers-color-scheme: dark) {
  /* Préparé pour le mode sombre futur */
}

/* Animations d'entrée progressives pour les sections */
.form-section:nth-child(1) { --section-index: 0; }
.form-section:nth-child(2) { --section-index: 1; }
.form-section:nth-child(3) { --section-index: 2; }
.form-section:nth-child(4) { --section-index: 3; }
.form-section:nth-child(5) { --section-index: 4; }
.form-section:nth-child(6) { --section-index: 5; }
.form-section:nth-child(7) { --section-index: 6; }
.form-section:nth-child(8) { --section-index: 7; }

/* Amélioration des focus pour l'accessibilité */
*:focus-visible {
  outline: 3px solid #FF9800;
  outline-offset: 2px;
  border-radius: 4px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 2;
}

.btn-example {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}

.btn-example:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.horizontal-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex-direction: row;
}

.horizontal-wrap .checkbox-label {
  flex: 0 0 auto;
}

.example-card {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid #10B981;
}

.example-text {
  font-style: italic;
  color: #4B5563;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
}

.example-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.example-list li {
  margin-bottom: 0.5rem;
  color: #059669;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

/* Example Modal Styles */
.example-full {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.example-section {
  background: #F9FAFB;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.example-section.highlight {
  background: #F0FDF4;
  border-color: #86EFAC;
}

.example-section h3 {
  font-size: 1rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.example-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem 1rem;
}

.example-item {
  font-size: 0.875rem;
  color: #4B5563;
}

.example-text {
  font-style: italic;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  font-size: 0.9375rem;
}

.example-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem 0;
}

.example-tags li {
  background: #E0E7FF;
  color: #4338CA;
  padding: 0.25rem 0.75rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
}

.context-note {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
}

.modal-footer-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  border-top: 1px solid #E5E7EB;
  padding-top: 1.5rem;
}

.btn-use-example {
  background: linear-gradient(135deg, #4F46E5 0%, #4338CA 100%);
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.btn-use-example:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
}

/* Confirmation Modal specific styles */
.confirm-modal {
  max-width: 400px;
}

.confirm-content {
  text-align: center;
  padding: 1rem 0;
}

.confirm-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.confirm-content p {
  margin: 0 0 0.5rem 0;
  color: #374151;
}

.confirm-content strong {
  color: #B91C1C;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #E5E7EB;
  background: #F9FAFB;
  border-radius: 0 0 16px 16px;
}

.btn-danger {
  background: #EF4444;
  box-shadow: 0 4px 6px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover {
  background: #DC2626;
}

/* Désactiver les animations pour les utilisateurs qui préfèrent */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.info-tooltip {
  display: inline-block;
  margin-left: 0.5rem;
  cursor: help;
  font-size: 0.9rem;
  position: relative;
  vertical-align: middle;
}

.info-tooltip::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #374151;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: normal;
  width: max-content;
  max-width: 250px;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 100;
  font-weight: normal;
  pointer-events: none;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  line-height: 1.4;
  text-align: center;
  margin-bottom: 5px;
}

/* Alignement spécifique pour éviter les coupures */
.info-tooltip.tooltip-left::after {
  left: -0.5rem;
  right: auto;
  transform: none;
  text-align: left;
}

.info-tooltip.tooltip-right::after {
  left: auto;
  right: -0.5rem;
  transform: none;
  text-align: left;
}

.info-tooltip:hover::after {
  visibility: visible;
  opacity: 1;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideIn 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #6B7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  line-height: 1;
}

.modal-close:hover {
  background: #F3F4F6;
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
}
</style>
